/**
 * @file Invoice.validation.ts
 * @description Yup validation schemas for Invoice create/edit forms
 * @module Invoices/Invoice
 */

import * as Yup from "yup";
import {
  optionalBoolean,
  optionalId,
  optionalNumber,
  optionalString,
  requiredId,
  startsWithField,
} from "helpers/validation";
import { FormDatasourceData, parseFormDatasourceData } from "./Invoice.datasource";
import type { InvoiceActionType } from "./Invoice.types";

/**
 * Base validation schema for creating a new invoice
 *
 * Required fields:
 * - Programme (programmesID)
 * - Supplier (suppliersID)
 * - Invoice Amount (invoiceAmount)
 * - Invoice Number (invoiceNumber)
 * - Invoice Date (invoiceDate) - cannot be in the future
 * - Invoice Received Date (invoiceReceivedDate) - cannot be in the future
 * - Invoice file (files)
 *
 * Optional fields:
 * - Project Manager (projectManagersID)
 * - Exchange Amount, Currency, and Rate
 */
export const validationSchemaCreate = Yup.object({
  exchangeAmount: optionalNumber({ typeErrorMessage: `A currency value is required` }),
  exchangeCurrencyID: optionalId(),
  exchangeRate: optionalNumber({ typeErrorMessage: `A decimal value is required` }),
  files: Yup.mixed().nullable().required("Please upload an Invoice file"),
  invoiceAmount: Yup.mixed()
    .nullable()
    .test(
      "is-required",
      "Please enter an Invoice Amount",
      (value) => value !== null && value !== ""
    ),
  invoiceNumber: Yup.string()
    .nullable()
    .test(
      "is-required",
      "Please enter an Invoice Number",
      (value) => value !== null && value !== ""
    ),
  invoiceCurrencyID: optionalId(),
  invoiceDate: Yup.string()
    .nullable()
    .test("is-required", "Please enter an Invoice Date", (value) => value !== null && value !== "")
    .test("not-in-future", "Invoice Date cannot be in the future", (value) => {
      if (!value) return true;
      const dateValue = new Date(value);
      return dateValue <= new Date();
    }),
  invoiceReceivedDate: Yup.string()
    .nullable()
    .test(
      "is-required",
      "Please enter an Invoice Received Date",
      (value) => value !== null && value !== ""
    )
    .test("not-in-future", "Invoice Received Date cannot be in the future", (value) => {
      if (!value) return true;
      const dateValue = new Date(value);
      return dateValue <= new Date();
    }),
  projectManagersID: optionalId(),
  programmesID: requiredId("Please select a Programme"),
  suppliersID: requiredId("Please select a Supplier"),
});

/**
 * Returns conditional validation schema for the Amount field
 *
 * Business Rule:
 * Amount is **required** when Payment Option is "Freight Claim"
 *
 * @param parsedFormDatasourceData - Parsed datasource containing listPaymentOptions
 * @returns Yup number schema with conditional requirement
 *
 * @example
 * // Amount is required if paymentOptionID === FREIGHT_CLAIM
 * const schema = getAmountSchema(parsedData);
 * // Validates: { paymentOptionID: 123, amount: 1000 } ✅
 * // Fails: { paymentOptionID: 123, amount: null } ❌ "Please provide Amount"
 */
export const getAmountSchema = (parsedFormDatasourceData: any) => {
  return parsedFormDatasourceData &&
    parsedFormDatasourceData.hasOwnProperty("listPaymentOptions") &&
    parsedFormDatasourceData["listPaymentOptions"].hasOwnProperty("FREIGHT_CLAIM")
    ? Yup.number()
        .nullable()
        .when("paymentOptionID", {
          is: parsedFormDatasourceData["listPaymentOptions"]["FREIGHT_CLAIM"],
          then: (schema) =>
            schema.required("Please provide Amount").typeError(`A currency value is required`),
          otherwise: (schema) => schema,
        })
    : optionalNumber({ typeErrorMessage: `A currency value is required` });
};

/**
 * Commented out - Prepay/Accrual Month validation
 *
 * Potential Future Business Rule:
 * Prepay/Accrual Month is **required** when Payment Option is "Prepay" or "Accrued Prior Month"
 *
 * Currently disabled but preserved for potential future use.
 */
// const getPrepayAccrualMonthSchema = (parsedFormDatasourceData: any) => {
//   return parsedFormDatasourceData &&
//     parsedFormDatasourceData.hasOwnProperty("listPaymentOptions") &&
//     (parsedFormDatasourceData["listPaymentOptions"].hasOwnProperty("PREPAY") ||
//      parsedFormDatasourceData["listPaymentOptions"].hasOwnProperty("ACCRUED_PRIOR_MONTH"))
//     ? Yup.string().nullable().when("paymentOptionID", {
//         is: (val) => val === parsedFormDatasourceData["listPaymentOptions"]["PREPAY"] ||
//                      val === parsedFormDatasourceData["listPaymentOptions"]["ACCRUED_PRIOR_MONTH"],
//         then: Yup.string().required("Please provide Prepay/Accrual Month"),
//       })
//     : Yup.string().nullable();
// };

/**
 * Returns conditional validation schema for the Notes field
 *
 * Business Rules:
 * Notes is **required** when:
 * 1. Immediate Pay field is "Yes" (true), OR
 * 2. Payment Option is one of: "Cost of Job", "Warranty", "Freight Claim", or "Bulk Invoice"
 *
 * @param parsedFormDatasourceData - Parsed datasource containing listPaymentOptions
 * @returns Yup string schema with conditional requirement based on paymentOptionID and immediatePay
 *
 * @example
 * // Notes required when immediatePay is true
 * const schema = getNotesSchema(parsedData);
 * // Validates: { immediatePay: true, notes: "Urgent payment required" } ✅
 * // Fails: { immediatePay: true, notes: null } ❌ "Please provide Notes"
 *
 * @example
 * // Notes required when paymentOptionID is COST_OF_JOB
 * // Validates: { paymentOptionID: 456, notes: "Cost adjustment" } ✅
 * // Fails: { paymentOptionID: 456, notes: "" } ❌ "Please provide Notes"
 */
export const getNotesSchema = (parsedFormDatasourceData: any) => {
  const listPaymentOptions = parsedFormDatasourceData?.listPaymentOptions || {};
  return optionalString().when(["paymentOptionID", "immediatePay"], {
    is: (paymentOptionID: any, immediatePay: any) =>
      paymentOptionID === listPaymentOptions.COST_OF_JOB ||
      paymentOptionID === listPaymentOptions.WARRANTY ||
      paymentOptionID === listPaymentOptions.FREIGHT_CLAIM ||
      paymentOptionID === listPaymentOptions.BULK_INVOICE ||
      immediatePay === true,
    then: (schema) => schema.required("Please provide Notes"),
    otherwise: (schema) => schema,
  });
};

/**
 * Returns validation schema for the Assigned To field with Finance Role restrictions
 *
 * Business Rules:
 * 1. Assigned To is **always required**
 * 2. Finance Role (ID: -1000) can **only** be selected when the status is:
 *    - "Approved"
 *    - "Processed"
 *    - "Returned"
 *    - "Rejected"
 *
 * If Finance Role is selected with any other status, validation fails with:
 * "Current Status cannot be assigned to Finance Role"
 *
 * @param formDatasourceData - Raw datasource containing listOfStatuses
 * @returns Yup number schema with status-dependent Finance Role validation
 *
 * @example
 * // Finance Role allowed with Approved status
 * const schema = getAssignedToSchema(formData);
 * // Validates: { assignedToID: -1000, statusID: 5 } where 5 = "Approved" ✅
 * // Fails: { assignedToID: -1000, statusID: 1 } where 1 = "Pending" ❌
 *
 * @example
 * // Non-Finance Role works with any status
 * // Validates: { assignedToID: 123, statusID: 1 } ✅ (any status OK)
 */
export const getAssignedToSchema = (formDatasourceData: any) => {
  const listStatuses = formDatasourceData?.listOfStatuses || [];

  const statusMap = listStatuses.reduce(
    (acc: Record<string, number>, status: { label: string; value: number }) => {
      acc[status.label.toLowerCase()] = status.value;
      return acc;
    },
    {}
  );

  return Yup.number()
    .nullable()
    .required("Please select an Assigned To")
    .test(
      "finance-role-status-check",
      "Current Status cannot be assigned to Finance Role",
      function (this: Yup.TestContext, value?: any) {
        const statusID = this.parent.statusID;
        const isFinanceRole = value === -1000; // Finance Role ID
        const isValidStatus =
          statusID === statusMap["approved"] ||
          statusID === statusMap["processed"] ||
          statusID === statusMap["returned"] ||
          statusID === statusMap["rejected"];

        // Finance Role is only valid if the status is one of the listed statuses
        return !(isFinanceRole && !isValidStatus);
      }
    );
};

/**
 * Returns conditional validation schema for the Payment Option field
 *
 * Business Rule:
 * Payment Option is **required** when actionType is "update-productmanager"
 *
 * This ensures Product Managers must select a payment option when updating invoices.
 *
 * @param actionType - The action type determining if validation is required
 * @returns Yup number schema with conditional requirement
 *
 * @example
 * // Payment Option required for Product Manager updates
 * const schema = getPaymentOptionSchema("update-productmanager");
 * // Validates: { paymentOptionID: 123 } ✅
 * // Fails: { paymentOptionID: null } ❌ "Please select a Payment Option"
 *
 * @example
 * // Payment Option optional for other action types
 * const schema = getPaymentOptionSchema("edit");
 * // Validates: { paymentOptionID: null } ✅ (optional)
 */
export const getPaymentOptionSchema = (actionType: InvoiceActionType) => {
  return actionType === "update-productmanager"
    ? requiredId("Please select a Payment Option")
    : optionalId();
};

/**
 * Returns conditional validation schema for the Supplier Feedback field
 *
 * Business Rule:
 * Supplier Feedback is **required** when actionType is "update-productmanager"
 *
 * This ensures Product Managers must provide supplier feedback when updating invoices, enabling better communication and tracking of supplier performance.
 *
 * @param actionType - The action type determining if validation is required
 * @returns Yup number schema with conditional requirement
 */
export const getSupplierFeedbackSchema = (actionType: InvoiceActionType) => {
  return actionType === "update-productmanager"
    ? requiredId("Please select a Supplier Feedback")
    : optionalId();
};

/**
 * Returns conditional validation schema for NCR Category field
 *
 * Business Rule:
 * NCR Category is **required** when Supplier Feedback is "Did Not Meet Expectations"
 *
 * @param parsedFormDatasourceData - Parsed datasource containing listSupplierFeedbackTypes
 * @returns Yup number schema with conditional requirement
 *
 * @example
 * const schema = getNCRCategorySchema(parsedData);
 * // DID_NOT_MEET_EXPECTATIONS selected + ncrCategoryTypesID null → ❌ "Please select an NCR Category"
 * // EXCEEDED_EXPECTATIONS selected + ncrCategoryTypesID null    → ✅ (not required)
 */
export const getNCRCategorySchema = (parsedFormDatasourceData: any) => {
  return parsedFormDatasourceData &&
    parsedFormDatasourceData.hasOwnProperty("listSupplierFeedbackTypes") &&
    parsedFormDatasourceData["listSupplierFeedbackTypes"].hasOwnProperty(
      "DID_NOT_MEET_EXPECTATIONS"
    )
    ? optionalId().when("supplierFeedbackTypesID", {
        is: parsedFormDatasourceData["listSupplierFeedbackTypes"]["DID_NOT_MEET_EXPECTATIONS"],
        then: (schema) =>
          schema
            .required("Please select an NCR Category")
            .typeError("Please select an NCR Category"),
        otherwise: (schema) => schema,
      })
    : optionalId();
};

/**
 * Returns conditional validation schema for NCR Description field
 *
 * Business Rule:
 * NCR Description is **required** when Supplier Feedback is "Did Not Meet Expectations"
 *
 * @param parsedFormDatasourceData - Parsed datasource containing listSupplierFeedbackTypes
 * @returns Yup string schema with conditional requirement
 *
 * @example
 * const schema = getNCRDescriptionSchema(parsedData);
 * // DID_NOT_MEET_EXPECTATIONS selected + ncrDescription null → ❌ "Please provide NCR Description"
 * // EXCEEDED_EXPECTATIONS selected + ncrDescription null     → ✅ (not required)
 */
export const getNCRDescriptionSchema = (parsedFormDatasourceData: any) => {
  return parsedFormDatasourceData &&
    parsedFormDatasourceData.hasOwnProperty("listSupplierFeedbackTypes") &&
    parsedFormDatasourceData["listSupplierFeedbackTypes"].hasOwnProperty(
      "DID_NOT_MEET_EXPECTATIONS"
    )
    ? optionalString().when("supplierFeedbackTypesID", {
        is: parsedFormDatasourceData["listSupplierFeedbackTypes"]["DID_NOT_MEET_EXPECTATIONS"],
        then: (schema) =>
          schema
            .required("Please provide NCR Description")
            .min(10, "NCR Description must be at least 10 characters"),
        otherwise: (schema) => schema,
      })
    : optionalString();
};

/**
 * Returns conditional validation schema for Compliment field
 *
 * Business Rule:
 * Compliment is **required** when Supplier Feedback is "Exceeded Expectations"
 *
 * @param parsedFormDatasourceData - Parsed datasource containing listSupplierFeedbackTypes
 * @returns Yup string schema with conditional requirement
 *
 * @example
 * const schema = getComplimentSchema(parsedData);
 * // EXCEEDED_EXPECTATIONS selected + compliment null         → ❌ "Please provide a Compliment"
 * // DID_NOT_MEET_EXPECTATIONS selected + compliment null     → ✅ (not required)
 */
export const getComplimentSchema = (parsedFormDatasourceData: any) => {
  return parsedFormDatasourceData &&
    parsedFormDatasourceData.hasOwnProperty("listSupplierFeedbackTypes") &&
    parsedFormDatasourceData["listSupplierFeedbackTypes"].hasOwnProperty("EXCEEDED_EXPECTATIONS")
    ? optionalString().when("supplierFeedbackTypesID", {
        is: parsedFormDatasourceData["listSupplierFeedbackTypes"]["EXCEEDED_EXPECTATIONS"],
        then: (schema) =>
          schema
            .required("Please provide a Compliment")
            .min(10, "Compliment must be at least 10 characters"),
        otherwise: (schema) => schema,
      })
    : optionalString();
};

/**
 * Returns comprehensive validation schema for editing an existing invoice
 *
 * Combines all field validations including:
 * - Basic fields (invoice number, dates, amounts)
 * - Conditional fields based on business rules:
 *   - Amount (required for Freight Claim)
 *   - Notes (required for Immediate Pay, or for Cost of Job / Warranty / Freight Claim / Bulk Invoice)
 *   - Assigned To (with Finance Role status restrictions)
 *   - Payment Option (required for Product Manager updates)
 *
 * Date Validations:
 * - Invoice Date and Invoice Received Date cannot be in the future
 *
 * Format Validations:
 * - Project Number must start with "P"
 * - Contract Number must start with "C"
 *
 * @param formDatasourceData - Raw datasource for status and role lookups
 * @param actionType - The action type (create, edit, update-productmanager, etc.)
 * @returns Complete Yup object schema for invoice editing
 *
 * @example
 * // Typical usage in form component
 * const validationSchema = getEditValidationSchema(datasource, "update-productmanager");
 * // Pass to Formik as validationSchema prop
 */
export const getEditValidationSchema = (
  formDatasourceData: FormDatasourceData | null,
  actionType: InvoiceActionType
) => {
  const parsedFormDatasourceData = parseFormDatasourceData(formDatasourceData);

  // Get custom schemas
  const amountSchema = getAmountSchema(parsedFormDatasourceData);
  const paymentOptionSchema = getPaymentOptionSchema(actionType);
  // const prepayAccrualMonthSchema = getPrepayAccrualMonthSchema(parsedFormDatasourceData);
  const notesSchema = getNotesSchema(parsedFormDatasourceData);
  const assignedToSchema = getAssignedToSchema(formDatasourceData);
  const supplierFeedbackSchema = getSupplierFeedbackSchema(actionType);
  const ncrCategorySchema = getNCRCategorySchema(parsedFormDatasourceData);
  const ncrDescriptionSchema = getNCRDescriptionSchema(parsedFormDatasourceData);
  const complimentSchema = getComplimentSchema(parsedFormDatasourceData);

  return Yup.object({
    id: optionalId(),
    invoiceNumber: optionalString(),
    apInvoiceNumber: optionalString(),
    apInvoiceDueDate: optionalString(),
    invoiceDate: Yup.string()
      .nullable()
      .test("not-in-future", "Invoice Date cannot be in the future", (value) => {
        if (!value) return true;
        const dateValue = new Date(value);
        return dateValue <= new Date();
      }),
    invoiceReceivedDate: Yup.string()
      .nullable()
      .test("not-in-future", "Invoice Received Date cannot be in the future", (value) => {
        if (!value) return true;
        const dateValue = new Date(value);
        return dateValue <= new Date();
      }),
    uploadedOn: optionalString(),
    agingDays: optionalNumber(),
    suppliersID: optionalId(),
    suppliersNumber: optionalString(),
    suppliersName: optionalString(),
    programsID: optionalId(),
    programsName: optionalString(),
    projectNumber: startsWithField("P", "Project Number"),
    contractNumber: startsWithField("C", "Contract Number"),
    siteIdentification: optionalString(),
    poNumber: optionalString(),
    grnNumber: optionalString(),
    projectManagersID: requiredId("Please select a Project Manager"),
    projectManager: optionalString(),
    statusID: requiredId("Please select a Status"),
    status: optionalString(),
    assignedToID: assignedToSchema,
    assignedTo: optionalString(),
    invoiceAmount: optionalNumber({ typeErrorMessage: `A currency value is required` }),
    invoiceCurrencyID: optionalId(),
    invoiceCurrency: optionalString(),
    minoritySpendAmount: optionalNumber({ typeErrorMessage: `A currency value is required` }),
    exchangeCurrencyID: optionalId(),
    exchangeCurrency: optionalString(),
    exchangeAmount: optionalNumber({ typeErrorMessage: `A currency value is required` }),
    exchangeRate: optionalNumber({ typeErrorMessage: `A decimal value is required` }),
    paymentOptionID: paymentOptionSchema,
    paymentOption: optionalString(),
    amount: amountSchema,
    accrualMonthsID: optionalId(),
    invoiceExpectedForPrepay: optionalString(),
    immediatePay: optionalBoolean(),
    notes: notesSchema,
    fileExtension: optionalString(),
    fileName: optionalString(),
    filePath: optionalString(),
    supplierFeedbackTypesID: supplierFeedbackSchema,
    ncrCategoryTypesID: ncrCategorySchema,
    ncrDescription: ncrDescriptionSchema,
    compliment: complimentSchema,
  });
};
